import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, Inject, OnInit } from '@angular/core';

import { ClientGroupCollectionQuery, ClientsImportNotificatorBase, EventNotificationsServiceBase, ProductGroupCollectionQuery, ProductsImportNotificatorBase, UnitQuery, UserCollectionQuery } from '@skylab-shared/business';
import { ReadonlyCheckerUnexpectedValueChangeEvent, RequestStatusCode401OccuredEvent, RequestStatusCode403OccuredEvent, RequestStatusCode404OccuredEvent, RequestStatusCode5xxOccuredEvent, RequestTimeoutOccured, RootContextBase } from '@skylab-shared/core';

import { Router, RouterOutlet } from '@angular/router';
import { ClientsImportNotificator, EventNotificationsService, ProductsImportNotificator } from '@skylab-angular-shared/business';
import { ApplicationInsightsService } from '@skylab-angular-shared/config';
import { EventBus, EventBusBase, IntervalWrapperService, L10n, L10nBase, RootContext } from '@skylab-angular-shared/core';
import { DialogInsService, DrawerOutletDirective, ToastService } from '@skylab-angular-shared/ui';
import { AppStates } from '@skylab-shared/scenes';
import { Constructor } from '@skylab-shared/types';
import { environment } from '@skylab/environments';
import { ExpiringLicensesAnnouncementComponent } from '../announcements/expiring-licenses-announcement/expiring-licenses-announcement.component';
import { PreparingDataAnnouncementComponent } from '../announcements/preparing-data-announcement/preparing-data-announcement.component';
import { AppBarComponent } from '../app-bar/app-bar.component';
import { AppLoadingOverlayComponent } from '../app-loading-overlay/app-loading-overlay.component';
import { NavigationButtonComponent } from '../navigation/navigation-button.component';
import { NavigationComponent } from '../navigation/navigation.component';
import { NoAdminsInfoDialogComponent } from '../technical-pages/no-admins-info-dialog.component';
import { SessionExpiredDialogComponent } from '../technical-pages/session-expired-dialog.component';

export type AngularApp = {
	stateNames: string[],
	component: Constructor<unknown>
};

@Component({
	selector: 'skl-layout',
	template: `
		<header class="main-header">
			<skl-app-bar>
				<skl-navigation-button
					[open]="navigation.isOpen()"
					(openChange)="navigation.setOpen($event)"
					slot="start"
				/>
			</skl-app-bar>
		</header>

		<aside>
			<skl-navigation #navigation />
		</aside>

		<main>
			<div class="main-content">
				<skl-app-loading-overlay />
				<skl-expiring-licenses-announcement />
				<skl-preparing-data-announcement />
				<router-outlet />
			</div>
			<skl-drawer-outlet />
		</main>
		<skl-menu-container class="components-container components-container--menu" />
	`,
	styles: `
		:host {
			display: grid;
			height: 100dvh;

			grid-template-areas:
				"header header"
				"sidebar content";

			grid-template-rows:
				min-content
				1fr;

			grid-template-columns:
				min-content
				1fr;

			& > header {
				grid-area: header;
				width: 100vw;
				isolation: isolate;
				z-index: 1;
			}

			& > aside {
				grid-area: sidebar;
				contain: layout;
				overflow: auto;
				isolation: isolate;
			}

			& > main {
				grid-area: content;
				contain: layout;
				min-height: 0;

				.main-content {
					display: flex;
					container-type: inline-size;
					flex-direction: column;
					height: 100%;
					overflow-y: auto;
					overflow-x: auto;
				}
			}
		}

		@media only screen and (max-width: 60rem) {
			:host {
				grid-template-areas:
					"header"
					"content";
				grid-template-columns: 1fr;

				& > aside {
					position: fixed;
					inset: 0;
					top: 3.25rem;
					contain: none;
					overflow: visible;
					z-index: 100;
					pointer-events: none;
				}
			}
		}
	`,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		AppBarComponent,
		NavigationComponent,
		NavigationButtonComponent,
		AppLoadingOverlayComponent,
		ExpiringLicensesAnnouncementComponent,
		PreparingDataAnnouncementComponent,
		RouterOutlet,
		DrawerOutletDirective
	],
})
export class LayoutComponent implements OnInit {
	private eventBusObservers: string[] = [];
	#router = inject(Router);

	public constructor(
		@Inject(EventBus) private readonly eventBus: EventBusBase,
		@Inject(L10n) private readonly l10n: L10nBase,
		@Inject(ClientsImportNotificator) private readonly clientImportNotificator: ClientsImportNotificatorBase,
		@Inject(ProductsImportNotificator) private readonly productsImportNotificator: ProductsImportNotificatorBase,
		@Inject(EventNotificationsService) private readonly eventNotifications: EventNotificationsServiceBase,
		@Inject(RootContext) private readonly rootContext: RootContextBase,
		private readonly insights: ApplicationInsightsService,
		private readonly dialogService: DialogInsService,

		private readonly intervalWrapperService: IntervalWrapperService,
		private readonly toastService: ToastService,
	) { }

	public ngOnInit(): void {
		this.eventNotifications.refreshInterval('onStartup');
		this.intervalWrapperService.run(),

			this.handleNoAdminsInfo();
		this.handleRequestErrors();
		this.observeCompletionOfImport();
	}

	public ngOnDestroy(): void {
		this.eventBus.deleteObserver(this.eventBusObservers);
	}

	public handleNoAdminsInfo(): void {
		this.rootContext.getAsync(UserCollectionQuery).then(users => {
			if (!users?.length) return;
			if (users.some(e => e.role === 'Administrator')) return;
			this.dialogService.showAsync({ component: NoAdminsInfoDialogComponent });
		}).catch(err => {
			console.error(err);
		});
	};


	public handleRequestErrors(): void {
		this.eventBusObservers.push(this.eventBus.observe(RequestStatusCode5xxOccuredEvent, (data: any) => {
			this.#router.navigate([AppStates.unexpectedError.url], { queryParams: { requestId: data?.requestId, errorCode: data?.errorCode } });
		}));

		this.eventBusObservers.push(this.eventBus.observe(RequestStatusCode403OccuredEvent, () => {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T('config.interceptor.403-status.message'),
				actions: [
					{
						text: this.l10n._T('config.interceptor.403-status.refresh'),
						handler: () => window.location.reload()
					}
				]
			});
		}));

		this.eventBusObservers.push(this.eventBus.observe(RequestStatusCode401OccuredEvent, () => {
			this.dialogService.showAsync({ component: SessionExpiredDialogComponent, cssClass: 'modal--small' })
		}));

		this.eventBusObservers.push(this.eventBus.observe(RequestStatusCode404OccuredEvent, () => {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T('config.interceptor.404-status.message'),
			});
		}));

		this.eventBusObservers.push(this.eventBus.observe(RequestTimeoutOccured, () => {
			this.toastService.showAsync({
				type: 'error',
				text: this.l10n._T('config.interceptor.timeout.message'),
			});
		}));

		this.eventBusObservers.push(this.eventBus.observe(ReadonlyCheckerUnexpectedValueChangeEvent, ({ message, propertyName }: { message: string; propertyName: string }) => {
			this.insights.trackEvent('FE_READONLY_CHECKER_TRIGGERED', { propertyName });

			if (environment.prod) return;
			this.toastService.showAsync({ type: 'error', text: message });
		}));
	}

	private observeCompletionOfImport(): void {
		this.clientImportNotificator.addObserver(_ => {
			this.rootContext.refresh(ClientGroupCollectionQuery);
		});

		this.productsImportNotificator.addObserver(_ => {
			this.rootContext.refresh(ProductGroupCollectionQuery);
			this.rootContext.refresh(UnitQuery);
		});
	}
}
